import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

export interface ContextualTourStep {
  elementId: string;
  title: string;
  description: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

export interface PageTourConfig {
  page: string;
  pageName: string;
  steps: ContextualTourStep[];
}

// Definição dos tours por página
export const PAGE_TOURS: Record<string, PageTourConfig> = {
  '/dashboard': {
    page: '/dashboard',
    pageName: 'Dashboard',
    steps: [
      {
        elementId: 'tour-stats-overview',
        title: 'Suas Estatísticas',
        description: 'Veja um resumo do seu uso da plataforma: conversas, ferramentas usadas e créditos disponíveis.',
        position: 'bottom'
      },
      {
        elementId: 'tour-smartcheck-card',
        title: 'SmartCheck Ativo',
        description: 'Acompanhe seu checklist em andamento e continue de onde parou.',
        position: 'left'
      },
      {
        elementId: 'tour-conversations',
        title: 'Conversas Recentes',
        description: 'Acesse rapidamente suas últimas conversas com a IA.',
        position: 'top'
      },
      {
        elementId: 'tour-points',
        title: 'Seus Pontos',
        description: 'Acompanhe sua pontuação e conquistas na gamificação.',
        position: 'left'
      }
    ]
  },
  '/ferramentas': {
    page: '/ferramentas',
    pageName: 'Ferramentas',
    steps: [
      {
        elementId: 'tour-sidebar-categories',
        title: 'Categorias',
        description: 'Navegue pelas categorias para encontrar a ferramenta ideal para sua necessidade.',
        position: 'right'
      },
      {
        elementId: 'tour-agents',
        title: 'Agentes GPT',
        description: 'Agentes especializados em diferentes áreas do marketing digital, prontos para te ajudar.',
        position: 'bottom'
      },
      {
        elementId: 'tour-prompts-section',
        title: 'Prompts de IA',
        description: 'Templates de prompts otimizados para gerar conteúdo de alta qualidade rapidamente.',
        position: 'bottom'
      },
      {
        elementId: 'tour-spreadsheets-section',
        title: 'Planilhas Inteligentes',
        description: 'Planilhas interativas para organizar e planejar suas estratégias de marketing.',
        position: 'bottom'
      },
      {
        elementId: 'tour-product-dna',
        title: 'DNA do Produto',
        description: 'Configure seu produto uma vez e a IA preencherá automaticamente suas informações em todas as ferramentas.',
        position: 'top'
      }
    ]
  },
  '/smartcheck': {
    page: '/smartcheck',
    pageName: 'SmartCheck',
    steps: [
      {
        elementId: 'tour-templates-grid',
        title: 'Templates Disponíveis',
        description: 'Escolha um template pronto ou crie um checklist personalizado com IA.',
        position: 'bottom'
      },
      {
        elementId: 'tour-create-checklist',
        title: 'Criar com IA',
        description: 'Descreva seu objetivo e a IA criará um checklist personalizado para você.',
        position: 'left'
      },
      {
        elementId: 'tour-my-checklists',
        title: 'Meus Checklists',
        description: 'Acompanhe o progresso dos seus checklists em andamento.',
        position: 'top'
      }
    ]
  },
  '/chat': {
    page: '/chat',
    pageName: 'Chat IA',
    steps: [
      {
        elementId: 'tour-chat-history',
        title: 'Histórico de Conversas',
        description: 'Acesse todas as suas conversas anteriores organizadas por data.',
        position: 'right'
      },
      {
        elementId: 'tour-model-selector',
        title: 'Seletor de Modelo',
        description: 'Escolha qual modelo de IA usar na conversa.',
        position: 'bottom'
      },
      {
        elementId: 'tour-chat-input',
        title: 'Área de Mensagem',
        description: 'Digite sua mensagem aqui. Use Shift+Enter para quebrar linha.',
        position: 'top'
      },
      {
        elementId: 'tour-tools-sidebar',
        title: 'Outras Ferramentas',
        description: 'Troque rapidamente entre diferentes agentes e prompts.',
        position: 'left'
      }
    ]
  }
};

export interface ContextualTourState {
  isActive: boolean;
  currentPage: string;
  currentStep: number;
  completedPages: string[];
  isLoading: boolean;
}

export function useContextualTour() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  
  const [state, setState] = useState<ContextualTourState>({
    isActive: false,
    currentPage: '',
    currentStep: 0,
    completedPages: [],
    isLoading: true
  });

  // Carregar páginas completadas do perfil
  useEffect(() => {
    if (profile) {
      const completedPages = (profile as any).page_tours_completed || [];
      setState(prev => ({
        ...prev,
        completedPages,
        isLoading: false
      }));
    } else {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [profile]);

  // Verificar se deve iniciar tour quando muda de página
  const currentPageConfig = PAGE_TOURS[location.pathname] || 
    (location.pathname.startsWith('/chat') ? PAGE_TOURS['/chat'] : null);

  const shouldStartTour = useCallback(() => {
    if (!user || !currentPageConfig) return false;
    if (state.isActive) return false;
    if (state.completedPages.includes(currentPageConfig.page)) return false;
    return true;
  }, [user, currentPageConfig, state.isActive, state.completedPages]);

  // Auto-iniciar tour ao entrar na página
  useEffect(() => {
    if (shouldStartTour() && currentPageConfig) {
      // Delay para garantir que a página carregou
      const timer = setTimeout(() => {
        setState(prev => ({
          ...prev,
          isActive: true,
          currentPage: currentPageConfig.page,
          currentStep: 0
        }));
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, shouldStartTour, currentPageConfig]);

  // Dados do step atual
  const currentStepData = currentPageConfig?.steps[state.currentStep] || null;
  const totalSteps = currentPageConfig?.steps.length || 0;

  // Avançar step
  const nextStep = useCallback(async () => {
    if (!currentPageConfig) return;

    if (state.currentStep >= totalSteps - 1) {
      // Completar tour da página
      await completeTour();
    } else {
      setState(prev => ({
        ...prev,
        currentStep: prev.currentStep + 1
      }));
    }
  }, [state.currentStep, totalSteps, currentPageConfig]);

  // Voltar step
  const prevStep = useCallback(() => {
    if (state.currentStep > 0) {
      setState(prev => ({
        ...prev,
        currentStep: prev.currentStep - 1
      }));
    }
  }, [state.currentStep]);

  // Completar tour da página
  const completeTour = useCallback(async () => {
    if (!user || !currentPageConfig) return;

    const newCompletedPages = [...state.completedPages, currentPageConfig.page];
    
    setState(prev => ({
      ...prev,
      isActive: false,
      completedPages: newCompletedPages
    }));

    // Salvar no banco
    try {
      await supabase
        .from('profiles')
        .update({ page_tours_completed: newCompletedPages })
        .eq('user_id', user.id);
    } catch (error) {
      console.error('Erro ao salvar tour completado:', error);
    }
  }, [user, currentPageConfig, state.completedPages]);

  // Pular tour
  const skipTour = useCallback(async () => {
    await completeTour();
  }, [completeTour]);

  // Iniciar tour manualmente
  const startTour = useCallback((page?: string) => {
    const targetPage = page || location.pathname;
    const config = PAGE_TOURS[targetPage] || 
      (targetPage.startsWith('/chat') ? PAGE_TOURS['/chat'] : null);
    
    if (config) {
      setState(prev => ({
        ...prev,
        isActive: true,
        currentPage: config.page,
        currentStep: 0
      }));
    }
  }, [location.pathname]);

  // Resetar tours (para debug)
  const resetAllTours = useCallback(async () => {
    if (!user) return;
    
    setState(prev => ({
      ...prev,
      completedPages: []
    }));

    try {
      await supabase
        .from('profiles')
        .update({ page_tours_completed: [] })
        .eq('user_id', user.id);
    } catch (error) {
      console.error('Erro ao resetar tours:', error);
    }
  }, [user]);

  return {
    ...state,
    currentStepData,
    totalSteps,
    currentPageConfig,
    nextStep,
    prevStep,
    skipTour,
    completeTour,
    startTour,
    resetAllTours,
    shouldStartTour: shouldStartTour()
  };
}
